import React from "react";
import { useQuery, useQueryClient } from "react-query";
import Fox from "../FoxLight.PNG";
import { fetchFamilyAnalysis } from "../api";
import { useNotifications } from "../helpers/useNotifications";
import Table from "../components/Table/Table";
import formatNumber from "../helpers/formatNumber";
import Grid from "../components/Grid/Grid";
import Spinner from "../components/Spinner/Spinner";
import Carousel from "../components/Carousel/Carousel";
import Charts from "../components/Charts/Charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstalod } from "@fortawesome/free-brands-svg-icons";
import {
  faMoneyBillTrendUp,
  faBitcoinSign,
  faCreditCard,
  faWallet,
  faBasketShopping,
  faPlugCircleBolt,
  faWheatAwn,
  faIndustry,
  faSackDollar,
  faCartShopping,
  faStarOfLife,
  faBuildingColumns,
  faComputer,
  faTowerCell,
  faNetworkWired,
  faTents,
  faEarthAfrica,
  faEarthAmericas,
  faEarthAsia,
  faEarthEurope,
  faEarthOceania,
  faHouseCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

const specialIconCategories = {
  Aktien: faMoneyBillTrendUp,
  ETF: faBasketShopping,
  Tagesgeld: faWallet,
  "P2P-Kredit": faCreditCard,
  Bausparer: faHouseCircleCheck,
  Krypto: faBitcoinSign,
  10: faPlugCircleBolt,
  15: faWheatAwn,
  20: faIndustry,
  25: faSackDollar,
  30: faCartShopping,
  35: faStarOfLife,
  40: faBuildingColumns,
  45: faComputer,
  50: faTowerCell,
  55: faNetworkWired,
  60: faTents,
  Europa: faEarthEurope,
  Nordamerika: faEarthAmericas,
  Südamerika: faEarthAmericas,
  Asien: faEarthAsia,
  Afrika: faEarthAfrica,
  Ozeanien: faEarthOceania,
};

const columns = [
  {
    label: "",
    field: "ticker",
    colWidth: "75%",
    rowValue: (_, row) => (
      <div className="dividend-payment-container">
        <div className="dividend-payment-image-container">
          {Object.keys(specialIconCategories)?.includes(row?.ticker) ? (
            <FontAwesomeIcon
              className="table-thumbnail"
              size="lg"
              height={45}
              width={45}
              style={{ marginRight: 8 }}
              icon={specialIconCategories?.[row.ticker]}
            />
          ) : (
            <img
              src={row?.logo || Fox}
              alt={row.ticker}
              className="table-thumbnail"
              height={45}
              width={45}
              style={{ marginRight: 8 }}
            />
          )}
        </div>
        <div className="dividend-payment-text-container">
          <div className="dividend-payment-date text-muted">
            <span className="trades-share-badge badge badge-info">
              {`${formatNumber(row?.percentage)} %`}
            </span>
          </div>
          <div className="dividend-payment-name text-light">
            {row?.name || row?.ticker}
          </div>
        </div>
      </div>
    ),
  },
  {
    label: "",
    field: "totalInvestment",
    colWidth: "25%",
    rowValue: (val, row) => {
      return (
        <div className="d-flex align-items-center p-3 justify-content-end">
          <span className="dividend-payment-value  badge badge-success">
            + {formatNumber(row?.value)} €
          </span>
        </div>
      );
    },
  },
];

function FamilyAnalytics({ title }) {
  const createNotification = useNotifications();
  const queryClient = useQueryClient();

  const [activeSlide, setActiveSlide] = React.useState("allocation");

  //#region data
  //* load portfolio charts
  const {
    data: dbPortfolioCharts,
    isLoading,
    isError,
    error,
  } = useQuery(["myFamilyAnalysis"], fetchFamilyAnalysis);

  //#endregion

  if (isError) createNotification("error", error.message);

  const allocationData = dbPortfolioCharts?.fullAllocation?.map((stock) => ({
    ticker: stock.ticker,
    name: stock.company?.name,
    logo: stock?.company?.logo,
    value: stock.currentValue || 0,
    absolute: stock.currentValue || 0,
    percentage: stock.percentage || 0,
  }));

  const countryAllocationData = dbPortfolioCharts?.countryAllocation?.map(
    (country) => ({
      ticker: country.ticker,
      name: country?.name,
      logo: country.logo,
      value: country.value || 0,
      absolute: country.value || 0,
      percentage: country.percentage || 0,
    }),
  );

  const continentAllocationData = dbPortfolioCharts?.continentAllocation?.map(
    (continent) => ({
      ticker: continent.ticker,
      name: continent?.name,
      logo: continent.logo,
      value: continent.value || 0,
      absolute: continent.value || 0,
      percentage: continent.percentage || 0,
    }),
  );

  const gicsAllocationData = dbPortfolioCharts?.gicsAllocation?.map(
    (sector) => ({
      ticker: sector.ticker,
      name: sector?.name,
      logo: sector.logo,
      value: sector.value || 0,
      absolute: sector.value || 0,
      percentage: sector.percentage || 0,
    }),
  );

  const assetClassAllocationData = dbPortfolioCharts?.assetClassAllocation?.map(
    (sector) => ({
      ticker: sector.ticker,
      name: sector?.name,
      logo: sector.logo,
      value: sector.value || 0,
      absolute: sector.value || 0,
      percentage: sector.percentage || 0,
    }),
  );

  const familyAllocationData = dbPortfolioCharts?.familyMemberAllocation?.map(
    (member) => ({
      name: member.userName,
      value: member.portfolioValue || 0,
      absolute: member.portfolioValue || 0,
      percentage: member.percentage || 0,
    }),
  );

  const dividendAllocationData = dbPortfolioCharts?.dividendByCompany?.map(
    (stock) => ({
      ticker: stock.ticker,
      name: stock.company?.name,
      logo: stock?.company?.logo,
      value: stock.currentValue || 0,
      absolute: stock.currentValue || 0,
      percentage: stock.percentage || 0,
    }),
  );

  const currentSelectedData = React.useMemo(() => {
    const map = {
      allocation: {
        data: allocationData,
        label: "Allokation",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
      assetclass: {
        data: assetClassAllocationData,
        label: "Assetklassen",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
      country: {
        data: countryAllocationData,
        label: "Länder",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
      continent: {
        data: continentAllocationData,
        label: "Kontinente",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
      gics: {
        data: gicsAllocationData,
        label: "Sektoren",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
      payments: {
        data: dividendAllocationData,
        label: "Erträge",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.payments,
      },
      family: {
        data: familyAllocationData,
        label: "Familie",
        defaultTotalValue: dbPortfolioCharts?.keyfacts?.value,
      },
    };

    return map[activeSlide] ?? null;
  }, [
    activeSlide,
    allocationData,
    countryAllocationData,
    continentAllocationData,
    gicsAllocationData,
    assetClassAllocationData,
    dividendAllocationData,
    familyAllocationData,
  ]);

  if (isLoading) return <Spinner />;

  return (
    <>
      <Charts.Pie
        showActiveKey
        dataset={currentSelectedData?.data?.filter(
          (c) => c.name !== "Nicht zuordenbar",
        )}
        colors={[
          "#1E7BC6",
          "#7266AE",
          "#A59CCE",
          "#D87FA0",
          "#E46682",
          "#E47A4C",
          "#F2B42B",
          "#4098A2",
          "#2E9EFF",
          "#7B8FA1",
        ]}
        startPosition={180}
        endPosition={0}
        innerRadius="75%"
        animated={true}
        label={`${formatNumber(currentSelectedData?.defaultTotalValue)} €`}
        metaLabel={currentSelectedData?.label}
        className="pie-chart-portfolio-container"
      />

      <Carousel onSlideChange={setActiveSlide}>
        <div
          data-key="allocation"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Allokation</h5>
        </div>

        <div
          data-key="assetclass"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Assetklassen</h5>
        </div>

        <div
          data-key="gics"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Sektoren</h5>
        </div>

        <div
          data-key="country"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Länder</h5>
        </div>

        <div
          data-key="continent"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Kontinente</h5>
        </div>

        <div
          data-key="payments"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Erträge</h5>
        </div>

        <div
          data-key="family"
          className="w-100 align-items-center text-light"
          style={{ height: "50px" }}
        >
          <h5>Familie</h5>
        </div>
      </Carousel>

      <div className="article-main" style={{ marginTop: "-20px" }}>
        <Grid>
          <h5 className="text-light text-center">
            {title}
            <button
              className="btn btn-link dismiss-active-or-focus delete-form-label"
              onClick={(e) => {
                e.preventDefault();
                queryClient.invalidateQueries(["myFamilyAnalysis"]);
              }}
            >
              <FontAwesomeIcon icon={faInstalod} />
            </button>
          </h5>
          <Table data={currentSelectedData?.data} columns={columns} />
        </Grid>
      </div>
    </>
  );
}

export default FamilyAnalytics;
